// src/components/common/InviteBusinessModal.jsx
// Centered "Invite a Business You Trust" modal. Drop in anywhere with
// {isOpen, onClose} — the component owns its own input/sending/success/error
// state. Backdrop click + × button + Cancel button all call onClose.
//
// Used by:
//   - DockingStation (+ button below the dock's main 5-button card)
//   - LocalUsersTeamList header (+ button next to "Find Teammates")
//
// Auth: the POST uses authFetch so an expired JWT is silently refreshed.

import React, { useState, useEffect } from 'react';
import { authFetch } from '../../utils/authFetch';

export default function InviteBusinessModal({ isOpen, onClose }) {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Reset state every time the modal opens — stale messages from a prior
  // session (e.g. dismissed mid-error) shouldn't carry over.
  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setError('');
      setSuccess(false);
      setSending(false);
    }
  }, [isOpen]);

  const handleSend = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setError('');
    const trimmed = String(email || '').trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    setSending(true);
    try {
      const res = await authFetch('/api/invite/business', {
        method: 'POST',
        body: JSON.stringify({ recipientEmail: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not send invitation.');
        setSending(false);
        return;
      }
      setSuccess(true);
      setSending(false);
      setTimeout(() => {
        onClose && onClose();
      }, 2000);
    } catch {
      setError('Network error. Please try again.');
      setSending(false);
    }
  };

  const handleCancel = () => {
    onClose && onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.35)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCancel();
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 12,
          boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
          padding: '20px 22px',
          width: 340,
          maxWidth: '92vw',
          border: '1px solid #e2e8f0',
        }}
        role="dialog"
        aria-label="Invite a business you trust"
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
            Invite a business you trust
          </h3>
          <button
            type="button"
            onClick={handleCancel}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 18,
              color: '#94a3b8',
              lineHeight: 1,
              padding: 0,
            }}
          >
            ×
          </button>
        </div>

        {success ? (
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#16a34a' }}>
            Invitation sent.
          </p>
        ) : (
          <form onSubmit={handleSend}>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
              Enter their <strong>business email</strong>. Personal email
              providers (gmail, yahoo, etc.) won't be able to complete signup,
              so encourage them to use the email tied to their business
              domain.
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@theirbusiness.com"
              disabled={sending}
              autoFocus
              style={{
                width: '100%',
                padding: '8px 10px',
                fontSize: 14,
                border: '1px solid #cbd5e1',
                borderRadius: 6,
                outline: 'none',
                marginBottom: 10,
                boxSizing: 'border-box',
              }}
            />
            {error && (
              <p style={{ margin: '0 0 10px', fontSize: 12, color: '#dc2626' }}>
                {error}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleCancel}
                disabled={sending}
                style={{
                  padding: '7px 14px',
                  fontSize: 13,
                  fontWeight: 500,
                  background: 'white',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  color: '#475569',
                  cursor: sending ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={sending || !email.trim()}
                style={{
                  padding: '7px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                  background: sending || !email.trim() ? '#94a3b8' : '#00aef7',
                  border: 'none',
                  borderRadius: 6,
                  color: 'white',
                  cursor: sending || !email.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {sending ? 'Sending…' : 'Send invite'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
